import { ChangeDetectionStrategy, Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { VehicleFeature } from '@hyundai/ng-common-lib';
import { M000C010ImageComponent } from '@hyundai/ui';
import { Store } from '@ngrx/store';
import { KeyValueComponent } from '../../../components/key-value/key-value.component';

@Component({
    selector: 'lib-vehicle-data-tab',
    standalone: true,
    imports: [M000C010ImageComponent, KeyValueComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    templateUrl: './vehicle-data-tab.component.html',
})
export class VehicleDataTabComponent {
    store = inject(Store);

    vehicle = this.store.selectSignal(VehicleFeature.vehicleState.selectLoadedDetail);

    vehicleHasTuv = computed(() => {
        const vehicleData = this.vehicle().vehicleData;
        return vehicleData.find((item) => item.key === 'TÜV/AU Neu' && item.value === 'Ja');
    });

    vehicleHasHu = computed(() => {
        const vehicleData = this.vehicle().vehicleData;
        return vehicleData.find(
            (item) => item.key === 'HU' && item.value && typeof item.value === 'string' && item.value.length > 0,
        );
    });

    data = computed(() => {
        const { details } = this.vehicle();
        return Object.entries(details).map(([key, value]) => ({ key, value }));
    });

    isPluginHybrid = computed(() => {
        const { isPluginHybrid } = this.vehicle();
        return isPluginHybrid;
    });

    // eslint-disable-next-line max-lines-per-function, max-statements
    envkvImage = computed(() => {
        const { envkv, isPluginHybrid } = this.vehicle();
        const envkvClass = envkv.find((e) => e.key === 'emissionsCombinedCO2Class');
        const envkvDischargedCO2Class = envkv.find((e) => e.key === 'emissionsDischargedCO2Class');
        if (!envkvClass || (envkvClass && !envkvClass.value)) {
            return;
        }

        if (isPluginHybrid && envkvDischargedCO2Class) {
            const tempClass = `${envkvClass.value}${envkvDischargedCO2Class.value}`.toLocaleUpperCase();
            const hybridClassImages = {
                AA: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20jv6xh807w63195u3gv',
                AB: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20fr35gm07uqm45qgx9l',
                AC: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20kz6xhq07w620kcnmk9',
                AD: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20hf35hg07uqjqf975yk',
                AE: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20jd9x8p07uobleptrk9',
                AF: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20hp35hl07uq8mgja093',
                AG: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20fb35ga07uq0z12o0s1',
                BA: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20fb35gc07uqpo0j6dsp',
                BB: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20hr9x8907uozjkgfc8f',
                BC: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20ei6xf707w636h98130',
                BD: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20gv9x7x07uoy47ohmby',
                BE: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20fs6xfw07w6mdjzvzao',
                BF: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20jp9x8y07uo3k0ubhi7',
                BG: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20gh6xg907w6dlor4wap',
                CA: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20gu6xgg07w63g1ybuxy',
                CB: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20fg6xfn07w636y67mo8',
                CC: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20es6xfc07w6rleys2sn',
                CD: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20fz35gt07uqi1at4vqr',
                CE: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20f135g507uq7ce9auo1',
                CF: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20k56xhg07w68nrztzhb',
                CG: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20gv6xgj07w69k0puo19',
                DA: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20k59x9607uo8j6si8h3',
                DB: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20m39x9q07uopvxesf4w',
                DC: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20fu6xfy07w6xbwkjokm',
                DD: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20kc9x9a07uodkca5tk9',
                DE: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20kd35ie07uqp9mr0l6n',
                DF: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20ob9x9w07uo59s29tb7',
                DG: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20jq6xh607w6k7yzcya0',
                EA: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20ja35i407uqcj5ujniq',
                EB: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20jg35i907uq0d15ny77',
                EC: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20ki6xhl07w607uxthvj',
                ED: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20g535gy07uqrq1ay52s',
                EE: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20j99x8m07uob7mx83y7',
                EF: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20l09x9g07uohlzkl4wj',
                EG: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20ff6xfl07w6m6qxll85',
                FA: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20li6xhv07w6h3dv7985',
                FB: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20gd35h307uq9no1qpey',
                FC: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20h935ha07uqpm4wboyp',
                FD: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20gr6xge07w6npck5a40',
                FE: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20kp35ij07uqqck9ro97',
                FF: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20kv35io07uqwkfr6rca',
                FG: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20l835iy07uqs6i15mzi',
                GA: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20jo9x8w07uoywez81eq',
                GB: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20hk9x8307uo8jnewxv4',
                GC: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20md6xi007w6wqaxmiju',
                GD: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20lr9x9l07uoy1k42luc',
                GE: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20l235it07uqucjjz29d',
                GF: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20je6xh107w6xgcrmxdl',
                GG: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3wz20ie35hu07uqbytqklrm',
            };
            return hybridClassImages[tempClass as keyof typeof hybridClassImages] || undefined;
        } else {
            const nonHybridClassImages = {
                A: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3eaxuj1fclc06w82qsqvu71',
                B: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3eaxujj6gae09ukl1wjc7p0',
                C: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3eaxukr5co707w4ewi701d9',
                D: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3eaxukd5co207w43e4esbs8',
                E: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3eaxuugexli07utubgaxij5',
                F: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3eaxuuhexlk07utqstp26do',
                G: 'https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cm3eaxujy5cnx07w48ifoefgl',
            };
            return (
                nonHybridClassImages[envkvClass.value.toLocaleUpperCase() as keyof typeof nonHybridClassImages] ||
                undefined
            );
        }
    });
}
